import _ from "underscore";

import { getDefaultFieldSettings } from "metabase/actions/utils";

import type {
  ActionFormSettings,
  FieldType,
  InputSettingType,
  NativeDatasetQuery,
  Parameter,
  ParameterType,
  WritebackParameter,
} from "metabase-types/api";
import type { TemplateTag, TemplateTagType } from "metabase-types/types/Query";

import type Metadata from "metabase-lib/metadata/Metadata";
import type NativeQuery from "metabase-lib/queries/NativeQuery";
import Question from "metabase-lib/Question";

type FieldTypeMap = Record<string, ParameterType>;

type TagTypeMap = Record<string, TemplateTagType>;

const fieldTypeToParameterTypeMap: FieldTypeMap = {
  string: "string/=",
  category: "string/=",
  number: "number/=",
};

const dateTypeToParameterTypeMap: FieldTypeMap = {
  date: "date/single",
  datetime: "date/single",
  monthyear: "date/month-year",
  quarteryear: "date/quarter-year",
};

const fieldTypeToTagTypeMap: TagTypeMap = {
  string: "text",
  category: "text",
  number: "number",
  date: "date",
};

// ActionCreator uses the NativeQueryEditor, which expects a Question object
// This utilities help us to work with the WritebackQueryAction as with a Question

export const newQuestion = (metadata: Metadata, databaseId?: number) => {
  return new Question(
    {
      dataset_query: {
        type: "native",
        database: databaseId ?? null,
        native: {
          query: "",
        },
      },
    },
    metadata,
  );
};

const getTagTypeFromFieldSettings = (fieldType: FieldType): TemplateTagType => {
  return fieldTypeToTagTypeMap[fieldType] ?? "text";
};

export const setTemplateTagTypesFromFieldSettings = (
  question: Question,
  settings: ActionFormSettings,
): Question => {
  const fields = settings.fields;
  const query = question.query() as NativeQuery;
  let tempQuestion = question.clone();

  query.templateTagsWithoutSnippets().forEach((tag: TemplateTag) => {
    const currentQuery = tempQuestion.query() as NativeQuery;
    const fieldType = fields[tag.id]?.fieldType ?? "string";
    const nextTag = {
      ...tag,
      type: getTagTypeFromFieldSettings(fieldType),
    };
    tempQuestion = tempQuestion.setQuery(
      currentQuery.setTemplateTag(tag.name, nextTag),
    );
  });

  return tempQuestion;
};

const getParameterTypeFromFieldSettings = (
  fieldType: FieldType,
  inputType: InputSettingType,
): ParameterType => {
  if (fieldType === "date") {
    return dateTypeToParameterTypeMap[inputType] ?? "date/single";
  }

  return fieldTypeToParameterTypeMap[fieldType] ?? "string/=";
};

export const setParameterTypesFromFieldSettings = (
  settings: ActionFormSettings,
  parameters: Parameter[],
): Parameter[] => {
  const fields = settings.fields;
  return parameters.map(parameter => {
    const field = fields[parameter.id];
    return {
      ...parameter,
      type: field
        ? getParameterTypeFromFieldSettings(field.fieldType, field.inputType)
        : "string/=",
    };
  });
};

export const removeOrphanSettings = (
  formSettings: ActionFormSettings,
  parameters: Parameter[],
): ActionFormSettings => {
  const parameterIds = parameters.map(parameter => parameter.id);
  return {
    ...formSettings,
    fields: _.pick(formSettings.fields, parameterIds),
  };
};

export const addMissingSettings = (
  settings: ActionFormSettings,
  parameters: Parameter[],
): ActionFormSettings => {
  const parameterIds = parameters.map(parameter => parameter.id);
  const fieldIds = Object.keys(settings.fields);
  const missingIds = _.difference(parameterIds, fieldIds);

  if (!missingIds.length) {
    return settings;
  }

  return {
    ...settings,
    fields: {
      ...settings.fields,
      ...Object.fromEntries(
        missingIds.map(id => [id, getDefaultFieldSettings({ id })]),
      ),
    },
  };
};

export const convertQuestionToAction = (
  question: Question,
  formSettings: ActionFormSettings,
) => {
  const cleanQuestion = setTemplateTagTypesFromFieldSettings(
    question,
    formSettings,
  );
  const parameters = setParameterTypesFromFieldSettings(
    formSettings,
    cleanQuestion.parameters(),
  );
  const visualization_settings = removeOrphanSettings(
    addMissingSettings(formSettings, parameters),
    parameters,
  );
  return {
    id: question.id(),
    name: question.displayName() as string,
    description: question.description(),
    dataset_query: question.datasetQuery() as NativeDatasetQuery,
    database_id: question.databaseId(),
    parameters: parameters as WritebackParameter[],
    visualization_settings,
  };
};
